import { t } from "i18next";
import React, { useCallback, useEffect, useRef } from "react";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { getVRMImage, getVRMName } from "~/vrm_utils/VRMUtils";
import { VRM, VRMLoaderPlugin } from "@pixiv/three-vrm";
import { VRMAnimationLoaderPlugin } from "@pixiv/three-vrm-animation";
import "~/css/AICharacterSettings.css";
import PromptSetting from "~/components/PromptSetting";
// キャラクターアイコンのサイズ（ピクセル）
const characterIconSize = 200;

interface AICharacterSettingsProps {
  loadedVrm: VRM | null;
  loadedVrmList: VRM[];
  handleCharacterSelect: (vrm: VRM) => void;
  isVoiceReady: boolean;
}

const AICharacterSettings: React.FC<AICharacterSettingsProps> = ({
  loadedVrm,
  loadedVrmList,
  handleCharacterSelect,
  isVoiceReady,
}) => {
  const canvasRef = useRef<HTMLCanvasElement[]>([]);
  const selectedName = loadedVrm ? getVRMName(loadedVrm) : null;
  const loader = new GLTFLoader();

  loader.register((parser) => {
    return new VRMLoaderPlugin(parser);
  });
  loader.register((parser) => {
    return new VRMAnimationLoaderPlugin(parser);
  });

  // VRMからアイコンを取得（初期化時に全て読み込み）
  const loadVRMIconList = async (vrmList: VRM[]) => {
    vrmList.forEach((vrm, index) => {
      const image = getVRMImage(vrm);
      if (!image || !canvasRef.current[index]) return;

      const canvas = canvasRef.current[index];
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      canvas.width = characterIconSize;
      canvas.height = characterIconSize;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(image, 0, 0, characterIconSize, characterIconSize);
    });
  };

  // Canvasの参照を設定するためのコールバック
  const setCanvasRef = useCallback((index: number) => {
    return (el: HTMLCanvasElement | null) => {
      if (el) {
        canvasRef.current[index] = el;
      }
    };
  }, []);

  useEffect(() => {
    if (loadedVrmList.length > 0) loadVRMIconList(loadedVrmList);
  }, [loadedVrmList]);

  return (
    <>
      <p className="character-select-text">{t("characterSelectText")}</p>
      <div className="character-content">
        {loadedVrmList.map((vrm, index) => {
          const isSelected = selectedName === getVRMName(vrm);
          return (
            <div key={index}>
              <canvas
                ref={setCanvasRef(index)}
                onClick={isVoiceReady ? () => handleCharacterSelect(vrm) : undefined}
                className={`character-icon ${isSelected ? "is-selected" : ""} ${isVoiceReady ? "clickable" : "not-clickable"}`}
              />
              <button
                className={`character-select-button ${isVoiceReady ? "clickable" : "not-clickable"}`}
                onClick={isVoiceReady ? () => handleCharacterSelect(vrm) : undefined}
                disabled={!isVoiceReady}
              >
                {getVRMName(vrm)}
              </button>
            </div>
          );
        })}
      </div>
      <div>
        <p className="title font-default-normal-20">{t("promptSettings")}</p>
        <PromptSetting selectedName={selectedName} />
      </div>
    </>
  );
};

export default AICharacterSettings;
